import type { TextareaHTMLAttributes } from "react"

import {
  useFormContext,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  type Path,
} from "react-hook-form"

import {
  FormField,
  FormControl,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Textarea } from "@/components/ui/textarea"

type CustomFormTextareaProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  placeholder: string
  className?: string
  disabled?: boolean
}

// Combine custom props with standard textarea attributes, omitting
// ones we handle explicitly or via react-hook-form
export type FormTextareaProps<T extends FieldValues = FieldValues> =
  CustomFormTextareaProps<T> &
    Omit<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      // Attributes handled by react-hook-form's field object or FormField
      | "name"
      | "value"
      | "onChange"
      | "onBlur"
      | "ref"
      | "defaultValue"

      // Attributes handled explicitly by our props
      | "placeholder"
      | "className"
      | "disabled"
    >

export function FormTextArea<T extends FieldValues = FieldValues>({
  name,
  placeholder,
  className: propClassName, // Rename to avoid conflict in scope
  disabled = false,
  ...otherHtmlAttributes
}: FormTextareaProps<T>) {
  const form = useFormContext<T>()

  const textareaProps = (field: ControllerRenderProps<T, Path<T>>) => {
    return {
      placeholder: placeholder,
      className: propClassName,
      // Other props (rows, cols, maxLength, autoComplete, aria-*, data-*, etc.)
      ...otherHtmlAttributes,
      // Spread the field props from react-hook-form after ...rest to
      // ensure react-hook-form's value, onChange, onBlur, ref take
      // precedence
      ...field,
      // field.disabled might be set by RHF's Fieldset/Form context
      disabled: disabled || field.disabled,
    }
  }

  return (
    <FormField
      control={form.control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem className="w-full">
          <FormControl>
            <Textarea {...textareaProps(field)} />
          </FormControl>
          <FormMessage className="form-error" />
        </FormItem>
      )}
    />
  )
}
